import React, { useState } from "react";
import clsx from "clsx";
import format from "date-fns/format";
import Button from "@material-ui/core/Button";
import DialogContent from "@material-ui/core/DialogContent";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogActions from "@material-ui/core/DialogActions";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import useTheme from "@material-ui/core/styles/useTheme";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";
import Breakpoints from "common/breakpoints";
import Loader from "components/Loader";
import { getMultiselectPreviewString } from "components/FormField/helpers";
import useStyles from "./styles";
import { IState } from "../constants";

export interface Props {
  values: IState;
  disabled: boolean;
  facilities: racehorse360.IFacility[];
  onConfirm: () => void;
  isLoading: boolean;
}

const getReviewRows = facilities => [
  {
    name: "title",
    label: "Title",
    render: value => value
  },
  {
    name: "facilityOptions",
    label: "Track",
    render: values =>
      getMultiselectPreviewString(
        values,
        facilities.map(f => ({
          id: f.id,
          name: f.name,
          options: f.trainingFacilities?.map(f => ({
            id: f.id,
            name: f.name
          }))
        }))
      )
  },
  {
    name: "startDate",
    label: "Start Date",
    render: value => (value ? format(value, "MM/dd/yyyy") : "-")
  },
  {
    name: "deadlineDate",
    label: "Deadline Date",
    render: value => (value ? format(value, "MM/dd/yyyy") : "-")
  },
  {
    name: "closeDate",
    label: "Close Date",
    render: value => (value ? format(value, "MM/dd/yyyy") : "-")
  },
  {
    name: "pdf",
    label: "Terms .PDF",
    render: value => value?.name || "-"
  }
];

const Review = (props: Props) => {
  const { values, disabled, facilities, onConfirm, isLoading } = props;
  const classes = useStyles();
  const theme = useTheme();
  const matchesUpSX720 = useMediaQuery(
    theme.breakpoints.up(Breakpoints.SX_720)
  );

  const [isOpen, setIsOpen] = useState<boolean>(false);

  const handleOpen = () => {
    setIsOpen(true);
  };

  const handleClose = () => {
    !isLoading && setIsOpen(false);
  };

  const handleConfirm = () => {
    onConfirm();
  };

  return (
    <>
      <Button
        classes={{
          root: classes.actionsReviewButton,
          disabled: classes.actionsReviewButtonDisabled
        }}
        disabled={disabled}
        onClick={handleOpen}
      >
        Review
      </Button>
      {!disabled && (
        <Dialog
          open={isOpen}
          classes={{ root: classes.dialogRoot, paper: classes.dialogPaper }}
          onClose={handleClose}
          disablePortal={true}
          fullScreen={!matchesUpSX720}
        >
          {isLoading && <Loader overlay />}
          <DialogTitle className={classes.dialogTitle}>
            {matchesUpSX720 ? (
              "Create New Intake Form?"
            ) : (
              <>
                <Button
                  className={clsx(classes.backButton, classes.backButtonHeader)}
                  onClick={handleClose}
                >
                  Back
                </Button>
                New Intake Form
              </>
            )}
          </DialogTitle>
          <DialogContent className={classes.dialogContent}>
            <p className={classes.dialogContentTitle}>Intake Form Details</p>
            <Table>
              <TableBody>
                {getReviewRows(facilities).map(row => (
                  <TableRow key={row.name} className={classes.reviewRow}>
                    <TableCell align="left">{row.label}</TableCell>
                    <TableCell
                      align="left"
                      className={clsx(classes.reviewRowCell, {
                        [classes.capitalize]: row.name === "facilityOptions"
                      })}
                    >
                      {row.render(values[row.name])}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </DialogContent>
          <DialogActions className={classes.dialogActions}>
            {matchesUpSX720 && (
              <Button
                className={clsx(classes.backButton, classes.backButtonFooter)}
                onClick={handleClose}
              >
                BACK
              </Button>
            )}
            <Button
              variant={matchesUpSX720 ? "text" : "contained"}
              color={"secondary"}
              className={classes.confirmButton}
              onClick={handleConfirm}
              disableElevation
            >
              Create New Intake Form
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default Review;
